/**
 * `sgCoverage` — SoundGuys editorial coverage riding on DealHunt `products_v2`
 * docs. Contract: dealhunt/docs/sg-coverage-product-docs.md — DealHunt ships
 * facts (deterministic selection), this app owns presentation.
 *
 * Parsing is tolerant by contract: a drifted shape must degrade to "no block"
 * with a visible signal, never crash the typeahead. Sub-keys validate
 * independently (per-line degradation), and anything unrecognized is dropped.
 */

export interface SgCoverageReview {
  url: string
  /** Nullable — an override-only review (writer-authored URL, no synced row). */
  title: string | null
  authorNames: string[]
}

export interface SgCoverageVersus {
  url: string
  title: string
  /** The other slot's participant name; null → fall back to the title. */
  opponentName: string | null
  /** Epoch millis; may be null. */
  publishedAt: number | null
}

export interface SgCoverageBestEntry {
  guideTitle: string
  guideUrl: string
  /** Verbatim roster role ("Top pick"); null when none — never fabricated. */
  role: string | null
  /** Epoch millis; may be null. */
  guideUpdatedAt: number | null
}

export interface SgCoverage {
  review?: SgCoverageReview
  versus?: SgCoverageVersus
  best?: SgCoverageBestEntry[]
}

export interface SgCoverageParse {
  /** null = no renderable coverage (absent, or nothing survived validation). */
  coverage: SgCoverage | null
  /** True when a present value failed shape validation (fully or per line) —
   *  the caller emits the logged/GA4 signal the contract requires. */
  invalid: boolean
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function optString(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null
}

function optNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function parseReview(v: unknown): SgCoverageReview | null {
  if (!isRecord(v)) {
    return null
  }
  const url = optString(v.url)
  if (!url) {
    return null
  }
  const authorNames = Array.isArray(v.authorNames)
    ? v.authorNames.filter((n): n is string => typeof n === 'string' && n.length > 0)
    : []
  return { url, title: optString(v.title), authorNames }
}

function parseVersus(v: unknown): SgCoverageVersus | null {
  if (!isRecord(v)) {
    return null
  }
  const url = optString(v.url)
  const title = optString(v.title)
  if (!url || !title) {
    return null
  }
  return {
    url,
    title,
    opponentName: optString(v.opponentName),
    publishedAt: optNumber(v.publishedAt),
  }
}

function parseBestEntry(v: unknown): SgCoverageBestEntry | null {
  if (!isRecord(v)) {
    return null
  }
  const guideTitle = optString(v.guideTitle)
  const guideUrl = optString(v.guideUrl)
  if (!guideTitle || !guideUrl) {
    return null
  }
  return {
    guideTitle,
    guideUrl,
    role: optString(v.role),
    guideUpdatedAt: optNumber(v.guideUpdatedAt),
  }
}

/** Absent input (`undefined`) is honest no-coverage — not invalid. */
export function parseSgCoverage(raw: unknown): SgCoverageParse {
  if (raw === undefined) {
    return { coverage: null, invalid: false }
  }
  if (!isRecord(raw)) {
    return { coverage: null, invalid: true }
  }
  let invalid = false
  const out: SgCoverage = {}
  if ('review' in raw) {
    const review = parseReview(raw.review)
    if (review) {
      out.review = review
    } else {
      invalid = true
    }
  }
  if ('versus' in raw) {
    const versus = parseVersus(raw.versus)
    if (versus) {
      out.versus = versus
    } else {
      invalid = true
    }
  }
  if ('best' in raw) {
    if (Array.isArray(raw.best)) {
      const best = raw.best.map(parseBestEntry).filter((e): e is SgCoverageBestEntry => e !== null)
      if (best.length > 0) {
        out.best = best
      }
      if (best.length !== raw.best.length) {
        invalid = true
      }
    } else {
      invalid = true
    }
  }
  const coverage = out.review || out.versus || out.best ? out : null
  return { coverage, invalid }
}
